/**
 * Sprint 62 (auth-frontend-impl-s62) — first-visit **cookies banner**.
 *
 * A non-modal bar pinned to the bottom of the canvas. Shown only on the
 * very first visit (when `Profile.cookiesChoice === null`) and dismissed
 * by either of the two buttons — Accept (records `'accepted'`) or Just
 * essentials (records `'essentials'`). The choice persists on the
 * profile blob (v4 schema) so a refresh never re-asks.
 *
 * Style intent (see `docs/AUTH_DESIGN.md` §5.1 / §5.2):
 *  - **Friendly first-person title**, not legalese.
 *  - Body names the "no tracking, no ads, no third parties" pledge — the
 *    same MARKETING_PLAN.md §7.1 differentiator we lean on elsewhere.
 *  - Both buttons same visual weight; "Just essentials" is a real choice,
 *    not a pre-selected dark pattern.
 *  - No "configure preferences" sub-menu: there are exactly two cookie
 *    classes (the future session cookie + nothing else), so a multi-tier
 *    consent UI for two switches would be theatre.
 *
 * UI-only — never touches `GameState`. Render + input mirror the
 * `puzzleSelect.ts` / `onboarding.ts` overlay conventions; the banner is
 * a sibling of `./start.ts` and may be visible at the same time on a
 * fresh-profile first visit (banner at bottom, modal centred — no
 * overlap).
 */

import { CONFIG } from '../config';
import { roundRect, withAlpha } from '../engine/util';
import { t } from '../i18n';
import { saveProfile, type CookiesChoice, type Profile } from '../game/profile';

const C = CONFIG.colors;
const FONT_DISPLAY = '"Chakra Petch", "Rajdhani", system-ui, sans-serif';
const FONT_MONO = '"Share Tech Mono", ui-monospace, "DejaVu Sans Mono", monospace';
const CW = CONFIG.canvas.width;
const CH = CONFIG.canvas.height;

/** A click region inside the banner — canvas-space pixels. */
interface Rect {
  x: number;
  y: number;
  w: number;
  h: number;
}

/**
 * Banner geometry — anchored to the bottom edge of the canvas. Sits
 * outside every existing HUD strip (the HUD lives at `y < hudHeight`),
 * over the playfield, and outside the COMMAND DOCK row, so the banner
 * never occludes wave / build controls. Height is generous enough to
 * fit a two-line body wrap at 12px in both EN and CS.
 */
const BANNER: Rect = { x: 16, y: CH - 92, w: CW - 32, h: 80 };

/** Button row, right-aligned inside the banner. */
const BTN_H = 32;
const BTN_GAP = 10;
const BTN_RIGHT_PAD = 18;
const BTN_BOTTOM_PAD = 14;

/** "Accept" button — primary cyan. */
const BTN_ACCEPT: Rect = {
  x: BANNER.x + BANNER.w - BTN_RIGHT_PAD - 120,
  y: BANNER.y + BANNER.h - BTN_BOTTOM_PAD - BTN_H,
  w: 120,
  h: BTN_H,
};
/** "Just essentials" button — neutral grey, equal weight. */
const BTN_ESSENTIALS: Rect = {
  x: BTN_ACCEPT.x - BTN_GAP - 158,
  y: BTN_ACCEPT.y,
  w: 158,
  h: BTN_H,
};

/** Resolve a click to a banner choice, or `null` for a miss. */
export type CookiesHit = { kind: 'accept' } | { kind: 'essentials' };

/** True while the cookies banner should render — first visit only. */
export function isCookiesBannerOpen(profile: Profile): boolean {
  return profile.cookiesChoice === null;
}

/**
 * Resolve a click at (`x`, `y`) to a {@link CookiesHit}, or `null`. Like
 * the start-screen modal, the banner is dismiss-by-choice — clicks
 * outside the two buttons are absorbed (the orchestrator may still want
 * to route them past, see `Game.handleInput` for the precedence rule).
 */
export function cookiesBannerHit(x: number, y: number): CookiesHit | null {
  if (inRect(BTN_ACCEPT, x, y)) return { kind: 'accept' };
  if (inRect(BTN_ESSENTIALS, x, y)) return { kind: 'essentials' };
  return null;
}

/**
 * Persist the player's cookies-banner choice on the profile. The chosen
 * value is the runtime contract surfaced by `docs/AUTH_DESIGN.md` §5.1 —
 * `'accepted'` enables the future session-cookie persistence path,
 * `'essentials'` keeps the sign-in to the current tab only (the future
 * Set-Cookie response will be dropped client-side).
 */
export function acceptCookiesChoice(
  profile: Profile,
  choice: CookiesChoice,
  name?: string,
): void {
  if (profile.cookiesChoice === choice) return;
  profile.cookiesChoice = choice;
  saveProfile(profile, name);
}

/** Draw the banner — call once per frame from the renderer while open. */
export function drawCookiesBanner(ctx: CanvasRenderingContext2D): void {
  const time = performance.now() / 1000;
  ctx.save();

  // Soft drop-shadow under the banner so it floats over the playfield.
  ctx.shadowBlur = 18;
  ctx.shadowColor = 'rgba(0, 0, 0, 0.6)';

  // Body — neon-tinted dark panel.
  const g = ctx.createLinearGradient(0, BANNER.y, 0, BANNER.y + BANNER.h);
  g.addColorStop(0, 'rgba(14, 22, 42, 0.97)');
  g.addColorStop(1, 'rgba(9, 13, 26, 0.97)');
  ctx.fillStyle = g;
  roundRect(ctx, BANNER.x, BANNER.y, BANNER.w, BANNER.h, 10);
  ctx.fill();
  ctx.shadowBlur = 0;

  // Outline + subtle pulsing accent.
  ctx.strokeStyle = withAlpha(C.accent, 0.55 + 0.18 * Math.sin(time * 2.2));
  ctx.lineWidth = 1.4;
  roundRect(ctx, BANNER.x, BANNER.y, BANNER.w, BANNER.h, 10);
  ctx.stroke();

  // Left accent rail (~3px) — the OVERVOLT pill signature.
  ctx.fillStyle = C.accent;
  roundRect(ctx, BANNER.x, BANNER.y, 3, BANNER.h, 1.5);
  ctx.fill();

  // Title row — small spark glyph + the friendly title.
  glowDot(ctx, BANNER.x + 22, BANNER.y + 24, 4.5, C.accent);
  ctx.fillStyle = C.hudText;
  ctx.textAlign = 'left';
  ctx.textBaseline = 'middle';
  ctx.font = `700 13px ${FONT_DISPLAY}`;
  ctx.fillText(t('cookies.title'), BANNER.x + 38, BANNER.y + 24);

  // Body — wraps to the area left of the buttons.
  ctx.font = `11px ${FONT_MONO}`;
  ctx.fillStyle = withAlpha(C.hudText, 0.86);
  ctx.textBaseline = 'top';
  const bodyMaxW = BTN_ESSENTIALS.x - BANNER.x - 56;
  const bodyLines = wrapText(ctx, t('cookies.body'), bodyMaxW);
  let by = BANNER.y + 38;
  for (const line of bodyLines.slice(0, 2)) {
    ctx.fillText(line, BANNER.x + 38, by);
    by += 14;
  }

  // Buttons — same visual weight (per §5.2: "Both buttons same weight").
  drawBannerButton(ctx, BTN_ESSENTIALS, t('cookies.essentials'), C.hudMuted);
  drawBannerButton(ctx, BTN_ACCEPT, t('cookies.accept'), C.accent);

  ctx.restore();
}

/** A neutral-fill button with a coloured outline + label. */
function drawBannerButton(
  ctx: CanvasRenderingContext2D,
  r: Rect,
  label: string,
  color: string,
): void {
  ctx.save();
  ctx.fillStyle = withAlpha(color, 0.14);
  roundRect(ctx, r.x, r.y, r.w, r.h, 6);
  ctx.fill();
  ctx.strokeStyle = withAlpha(color, 0.75);
  ctx.lineWidth = 1.3;
  roundRect(ctx, r.x, r.y, r.w, r.h, 6);
  ctx.stroke();
  ctx.fillStyle = color === C.hudMuted ? C.hudText : color;
  ctx.font = `700 12px ${FONT_DISPLAY}`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(label, r.x + r.w / 2, r.y + r.h / 2 + 1);
  ctx.restore();
}

// --- shared helpers ---------------------------------------------------------

function inRect(b: Rect, x: number, y: number): boolean {
  return x >= b.x && x <= b.x + b.w && y >= b.y && y <= b.y + b.h;
}

/** A crisp dot wrapped in a soft glow (mirrors `onboarding.ts` `glowDot`). */
function glowDot(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  r: number,
  color: string,
): void {
  ctx.save();
  ctx.shadowBlur = 8;
  ctx.shadowColor = color;
  ctx.fillStyle = color;
  ctx.beginPath();
  ctx.arc(x, y, r, 0, Math.PI * 2);
  ctx.fill();
  ctx.restore();
}

/** Greedy wrap helper — mirrors `onboarding.ts` `wrapText`. */
function wrapText(ctx: CanvasRenderingContext2D, text: string, maxWidth: number): string[] {
  const words = text.split(' ');
  const lines: string[] = [];
  let line = '';
  for (const word of words) {
    const candidate = line ? `${line} ${word}` : word;
    if (line && ctx.measureText(candidate).width > maxWidth) {
      lines.push(line);
      line = word;
    } else {
      line = candidate;
    }
  }
  if (line) lines.push(line);
  return lines;
}
